import { Component } from '@theme/component';
import { FilterUpdateEvent } from '@theme/events';
import { sectionRenderer } from '@theme/section-renderer';
import { debounce } from '@theme/utilities';

const DEFAULT_CURRENCY_DECIMALS = 2;
const CURRENCY_DECIMALS = {
  BIF: 0,
  CLP: 0,
  DJF: 0,
  GNF: 0,
  ISK: 0,
  JPY: 0,
  KMF: 0,
  KRW: 0,
  PYG: 0,
  RWF: 0,
  UGX: 0,
  VND: 0,
  VUV: 0,
  XAF: 0,
  XOF: 0,
  XPF: 0,
};

const FILTER_PARAM_PREFIX = 'filter.';

/**
 * @typedef {Object} FacetStatusRefs
 * @property {HTMLElement} facetStatus - The facet status element
 * @property {HTMLTemplateElement} [moneyFormat] - The money format template
 */

/**
 * Handles facet status display.
 * @extends {Component<FacetStatusRefs>}
 */
class FacetStatusComponent extends Component {
  /**
   * Updates the list summary.
   * @param {HTMLInputElement[]} checkedInputElements
   */
  updateListSummary(checkedInputElements) {
    const checkedInputElementsCount = checkedInputElements.length;

    this.getAttribute('facet-type') === 'swatches'
      ? this.#updateSwatchSummary(checkedInputElements, checkedInputElementsCount)
      : this.#updateBubbleSummary(checkedInputElements, checkedInputElementsCount);
  }

  /**
   * Updates the swatch summary.
   * @param {HTMLInputElement[]} checkedInputElements
   * @param {number} checkedInputElementsCount
   */
  #updateSwatchSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    if (!facetStatus) return;

    facetStatus.classList.remove('bubble', 'facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (checkedInputElementsCount > 3) {
      facetStatus.innerHTML = checkedInputElementsCount.toString();
      facetStatus.classList.add('bubble', 'facets__bubble');
      return;
    }

    facetStatus.innerHTML = Array.from(checkedInputElements)
      .map((inputElement) => {
        const swatch = inputElement.parentElement?.querySelector('span.swatch');
        return swatch?.outerHTML ?? '';
      })
      .join('');
  }

  /**
   * Updates the bubble summary.
   * @param {HTMLInputElement[]} checkedInputElements
   * @param {number} checkedInputElementsCount
   */
  #updateBubbleSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    if (!facetStatus) return;

    facetStatus.classList.remove('bubble', 'facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (checkedInputElementsCount === 1) {
      facetStatus.innerHTML = checkedInputElements[0]?.dataset.label ?? checkedInputElements[0]?.ariaLabel ?? '';
      return;
    }

    facetStatus.innerHTML = checkedInputElementsCount.toString();
    facetStatus.classList.add('bubble', 'facets__bubble');
  }

  /**
   * Updates the price summary.
   * @param {HTMLInputElement} minInput
   * @param {HTMLInputElement} maxInput
   */
  updatePriceSummary(minInput, maxInput) {
    const minInputValue = minInput.value;
    const maxInputValue = maxInput.value;
    const { facetStatus } = this.refs;
    if (!facetStatus) return;

    if (!minInputValue && !maxInputValue) {
      facetStatus.innerHTML = '';
      return;
    }

    const minInputNum = this.#parseCents(minInputValue, '0');
    const maxInputNum = this.#parseCents(maxInputValue, facetStatus.dataset.rangeMax);
    facetStatus.innerHTML = `${this.#formatMoney(minInputNum)}-${this.#formatMoney(maxInputNum)}`;
  }

  /**
   * @param {string} value
   * @param {string | undefined} fallback
   * @returns {number}
   */
  #parseCents(value, fallback = '0') {
    if (!value) return parseInt(fallback, 10);

    const parts = value.trim().split(/[^0-9]/);

    if (parts.length > 1) {
      const fractionStr = parts.pop() ?? '0';
      const wholeStr = parts.join('');
      const whole = parseInt(wholeStr, 10) || 0;
      let fraction = parseInt(fractionStr || '0', 10) || 0;

      fraction = fraction * Math.pow(10, Math.max(0, 2 - fractionStr.length));

      return whole * 100 + fraction;
    }

    const fallbackCents = parseInt(fallback, 10);
    if (parts.length === 0 || parts[0] === '') return fallbackCents;

    return parseInt(parts[0], 10) * 100;
  }

  /**
   * @param {number} moneyValue
   * @returns {string}
   */
  #formatMoney(moneyValue) {
    if (!(this.refs.moneyFormat instanceof HTMLTemplateElement)) return '';

    const template = this.refs.moneyFormat.content.textContent || '{{amount}}';
    const currency = this.refs.facetStatus?.dataset.currency || '';

    return template.replace(/{{\s*([^}]+)\s*}}/g, (_, placeholderMatch) => {
      const placeholder = String(placeholderMatch).trim();

      if (placeholder === 'currency') return currency;

      let thousandsSeparator = ',';
      let decimalSeparator = '.';
      let precision = CURRENCY_DECIMALS[currency.toUpperCase()] ?? DEFAULT_CURRENCY_DECIMALS;

      if (placeholder === 'amount_no_decimals') {
        precision = 0;
      } else if (placeholder === 'amount_with_comma_separator') {
        thousandsSeparator = '.';
        decimalSeparator = ',';
      } else if (placeholder === 'amount_no_decimals_with_comma_separator') {
        thousandsSeparator = '.';
        precision = 0;
      } else if (placeholder === 'amount_no_decimals_with_space_separator') {
        thousandsSeparator = ' ';
        precision = 0;
      } else if (placeholder === 'amount_with_space_separator') {
        thousandsSeparator = ' ';
        decimalSeparator = ',';
      } else if (placeholder === 'amount_with_period_and_space_separator') {
        thousandsSeparator = ' ';
        decimalSeparator = '.';
      } else if (placeholder === 'amount_with_apostrophe_separator') {
        thousandsSeparator = "'";
        decimalSeparator = '.';
      }

      return this.#formatCents(moneyValue, thousandsSeparator, decimalSeparator, precision);
    });
  }

  /**
   * @param {number} moneyValue
   * @param {string} thousandsSeparator
   * @param {string} decimalSeparator
   * @param {number} precision
   * @returns {string}
   */
  #formatCents(moneyValue, thousandsSeparator, decimalSeparator, precision) {
    const roundedNumber = (moneyValue / 100).toFixed(precision);
    let [whole, fraction = ''] = roundedNumber.split('.');

    whole = (whole || '0').replace(/\d(?=(\d\d\d)+(?!\d))/g, (digit) => digit + thousandsSeparator);

    return precision <= 0 ? whole : whole + decimalSeparator + fraction.padEnd(precision, '0');
  }

  clearSummary() {
    if (this.refs.facetStatus) this.refs.facetStatus.innerHTML = '';
  }
}

/**
 * Handles a facet form and refreshes the collection section without a full page reload.
 * @extends {Component<{ facetsForm: HTMLFormElement }>}
 */
class FacetsFormComponent extends Component {
  requiredRefs = ['facetsForm'];

  connectedCallback() {
    super.connectedCallback();
    this.refs.facetsForm.addEventListener('submit', this.#handleSubmit);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.facetsForm?.removeEventListener('submit', this.#handleSubmit);
  }

  #handleSubmit = (event) => {
    event.preventDefault();
    this.updateResultsFromForm();
  };

  updateResultsFromForm = debounce(() => {
    this.updateResultsFromUrl(buildUrlFromForm(this.refs.facetsForm));
  }, 150);

  /**
   * @param {URL} url
   */
  async updateResultsFromUrl(url) {
    const sectionId = this.getAttribute('section-id');
    if (!sectionId) return;

    url.searchParams.delete('page');
    url.searchParams.delete('section_id');
    window.history.pushState({}, '', url.toString());
    document.dispatchEvent(new FilterUpdateEvent(new URLSearchParams(url.search)));

    await sectionRenderer.renderSection(sectionId, { cache: false, url: new URL(url.toString()) });
  }
}

/**
 * Handles checkbox/list filter changes.
 * @extends {Component<{ facetInputs: HTMLInputElement[] }>}
 */
class FacetInputsComponent extends Component {
  updateFilters() {
    this.#updateStatus();
    this.#formComponent?.updateResultsFromForm();
  }

  clearFilter(event) {
    if (!isActivationEvent(event)) return;

    for (const input of this.#inputs) {
      input.checked = false;
    }

    this.#updateStatus();
    this.#formComponent?.updateResultsFromForm();
  }

  handleKeyDown(event) {
    if (!(event instanceof KeyboardEvent) || !['Enter', ' '].includes(event.key)) return;

    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const input = target.matches('input') ? target : target.querySelector('input');
    if (!(input instanceof HTMLInputElement) || input.disabled) return;

    event.preventDefault();
    input.click();
  }

  prefetchPage() {}

  cancelPrefetchPage() {}

  #updateStatus() {
    const status = this.closest('.facets__item')?.querySelector('facet-status-component');
    if (status instanceof FacetStatusComponent) {
      status.updateListSummary(this.#inputs.filter((input) => input.checked));
    }
  }

  get #inputs() {
    return Array.isArray(this.refs.facetInputs) ? this.refs.facetInputs : [];
  }

  get #formComponent() {
    return this.closest('facets-form-component');
  }
}

/**
 * Handles price range filter changes.
 * @extends {Component<{ minInput: HTMLInputElement, maxInput: HTMLInputElement }>}
 */
class PriceFacetComponent extends Component {
  updatePriceFilterAndResults() {
    this.#normalizeInputs();
    this.#updateStatus();
    this.#formComponent?.updateResultsFromForm();
  }

  clearFilter(event) {
    if (!isActivationEvent(event)) return;

    this.refs.minInput.value = '';
    this.refs.maxInput.value = '';
    this.#updateStatus();
    this.#formComponent?.updateResultsFromForm();
  }

  #normalizeInputs() {
    for (const input of [this.refs.minInput, this.refs.maxInput]) {
      input.value = input.value.trim();
    }
  }

  #updateStatus() {
    const status = this.closest('.facets__item')?.querySelector('facet-status-component');
    if (status instanceof FacetStatusComponent) {
      status.updatePriceSummary(this.refs.minInput, this.refs.maxInput);
    }
  }

  get #formComponent() {
    return this.closest('facets-form-component');
  }
}

/**
 * Handles a single-filter clear button.
 * @extends {Component}
 */
class FacetClearComponent extends Component {
  clearFilter(event) {
    if (!isActivationEvent(event)) return;

    const facet = this.closest('facet-inputs-component, price-facet-component');
    if (facet instanceof FacetInputsComponent || facet instanceof PriceFacetComponent) {
      facet.clearFilter(event);
    }
  }
}

/**
 * Handles active filter pill and clear-all buttons.
 * @extends {Component}
 */
class FacetRemoveComponent extends Component {
  /**
   * @param {{ form?: string } | Event} data
   * @param {Event} [event]
   */
  removeFilter(data, event) {
    const activationEvent = event ?? data;
    if (!isActivationEvent(activationEvent)) return;

    const formId = !(data instanceof Event) ? data?.form : '';
    const formComponent = getFormComponent(formId, this);
    const rawUrl = this.dataset.url;

    if (!formComponent || !rawUrl) return;

    formComponent.updateResultsFromUrl(new URL(rawUrl, window.location.href));
  }
}

/**
 * Handles sorting controls.
 * @extends {Component<{ details: HTMLDetailsElement, summary: HTMLElement, listbox: HTMLElement }>}
 */
class SortingFilterComponent extends Component {
  updateFilterAndSorting(event) {
    const target = event.target;
    if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return;

    this.#syncSelectedOption(target.value);
    this.#formComponent?.updateResultsFromForm();
  }

  handleToggle() {
    if (this.refs.summary && this.refs.details) {
      this.refs.summary.setAttribute('aria-expanded', this.refs.details.open ? 'true' : 'false');
    }
  }

  handleKeyDown(event) {
    if (!(event instanceof KeyboardEvent)) return;

    const options = Array.from(this.querySelectorAll('[role="option"]'));
    const currentIndex = options.findIndex((option) => option === document.activeElement);

    if (event.key === 'Enter' || event.key === ' ') {
      const input = event.target instanceof HTMLElement ? event.target.querySelector('input') : null;
      if (input instanceof HTMLInputElement) {
        event.preventDefault();
        input.click();
      }
    } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const offset = event.key === 'ArrowDown' ? 1 : -1;
      const nextIndex = Math.max(0, Math.min(options.length - 1, currentIndex + offset));
      if (options[nextIndex] instanceof HTMLElement) options[nextIndex].focus();
    }
  }

  /**
   * @param {string} value
   */
  #syncSelectedOption(value) {
    for (const option of this.querySelectorAll('[role="option"]')) {
      const input = option.querySelector('input');
      const selected = input instanceof HTMLInputElement && input.value === value;
      option.setAttribute('aria-selected', selected ? 'true' : 'false');
      option.setAttribute('tabindex', selected ? '0' : '-1');
    }
  }

  get #formComponent() {
    return this.closest('facets-form-component');
  }
}

/**
 * @param {HTMLFormElement} form
 * @returns {URL}
 */
function buildUrlFromForm(form) {
  const url = new URL(form.action || window.location.href, window.location.href);
  const params = new URLSearchParams(url.search);
  const formData = new FormData(form);
  const formKeys = new Set();

  for (const key of params.keys()) {
    if (key.startsWith(FILTER_PARAM_PREFIX) || key === 'sort_by' || key === 'page' || key === 'section_id') {
      formKeys.add(key);
    }
  }

  for (const key of formData.keys()) {
    formKeys.add(key);
  }

  for (const key of formKeys) {
    params.delete(key);
  }

  for (const [key, value] of formData.entries()) {
    if (typeof value === 'string' && value.trim() !== '') {
      params.append(key, value);
    }
  }

  url.search = params.toString();
  url.hash = '';

  return url;
}

/**
 * @param {string | undefined} formId
 * @param {Element} source
 * @returns {FacetsFormComponent | null}
 */
function getFormComponent(formId, source) {
  if (formId) {
    const element = document.getElementById(formId);
    if (element instanceof FacetsFormComponent) return element;
  }

  return source.closest('facets-form-component') ?? document.querySelector('facets-form-component');
}

/**
 * @param {Event | unknown} event
 * @returns {event is Event}
 */
function isActivationEvent(event) {
  if (!(event instanceof Event)) return false;
  if (!(event instanceof KeyboardEvent)) return true;

  return event.key === 'Enter' || event.key === ' ';
}

if (!customElements.get('facet-status-component')) {
  customElements.define('facet-status-component', FacetStatusComponent);
}

if (!customElements.get('facets-form-component')) {
  customElements.define('facets-form-component', FacetsFormComponent);
}

if (!customElements.get('facet-inputs-component')) {
  customElements.define('facet-inputs-component', FacetInputsComponent);
}

if (!customElements.get('price-facet-component')) {
  customElements.define('price-facet-component', PriceFacetComponent);
}

if (!customElements.get('facet-clear-component')) {
  customElements.define('facet-clear-component', FacetClearComponent);
}

if (!customElements.get('facet-remove-component')) {
  customElements.define('facet-remove-component', FacetRemoveComponent);
}

if (!customElements.get('sorting-filter-component')) {
  customElements.define('sorting-filter-component', SortingFilterComponent);
}
